import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps,
} from '@xyflow/react';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils/cn';

type EdgeKind = 'dtmf' | 'default';

const KIND_STYLES: Record<EdgeKind, string> = {
  dtmf: 'border-violet-300 bg-violet-50 text-violet-900',
  default: 'border-slate-300 bg-slate-50 text-slate-700',
};

/**
 * Custom edge with a small × button hovering on the curve — clicking it
 * removes the edge. Edge labels (e.g. "Key 5" for DTMF branches) ride along
 * the curve via EdgeLabelRenderer with a colored pill keyed by `data.kind`.
 */
export function IvrEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  label,
  markerEnd,
  data,
}: EdgeProps) {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const onRemove = (data as { onRemove?: (id: string) => void } | undefined)
    ?.onRemove;
  const kind = ((data as { kind?: EdgeKind } | undefined)?.kind ??
    'default') as EdgeKind;

  return (
    <>
      <BaseEdge id={id} path={edgePath} markerEnd={markerEnd} />
      <EdgeLabelRenderer>
        <div
          style={{
            position: 'absolute',
            transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
            pointerEvents: 'all',
          }}
          className="flex items-center gap-1"
        >
          {label && (
            <span
              className={cn(
                'rounded-full border px-2 py-0.5 text-[10px] font-semibold tracking-tight shadow-sm',
                KIND_STYLES[kind],
              )}
            >
              {String(label)}
            </span>
          )}
          {onRemove && (
            <button
              type="button"
              onClick={() => onRemove(id)}
              className="rounded-full bg-card p-0.5 text-muted-foreground shadow-sm transition-colors hover:bg-destructive hover:text-destructive-foreground"
              aria-label="Remove connection"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      </EdgeLabelRenderer>
    </>
  );
}
